import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, CheckCircle2, Target, Shield, Eye, Users, Award, Download } from 'lucide-react';
import { aboutContent, industries, certifications, clients } from '../data/mock';

const AboutPage = () => {
  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="pt-28 lg:pt-36 pb-16 bg-[#0a1628]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#4fd1c5] text-sm font-semibold tracking-[0.15em] uppercase mb-4">
              About Grenew
            </p>
            <h1 className="text-4xl lg:text-5xl font-light text-white mb-6">
              {aboutContent.headline}
            </h1>
            <p className="text-lg text-gray-400 leading-relaxed">
              {aboutContent.paragraphs[0]}
            </p>
          </div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img 
                src="https://images.pexels.com/photos/209251/pexels-photo-209251.jpeg"
                alt="Grenew Facility"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
            <div>
              {aboutContent.paragraphs.slice(1).map((para, i) => (
                <p key={i} className="text-gray-600 leading-relaxed mb-4">
                  {para}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Facility */}
      <section className="py-16 lg:py-24 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#4fd1c5] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
                {aboutContent.facility.headline}
              </p>
              <h2 className="text-3xl lg:text-4xl font-light text-[#0a1628] mb-4">
                World-Class <span className="font-semibold">Infrastructure</span>
              </h2>
              <div className="flex items-center gap-2 text-gray-500 mb-6">
                <MapPin className="w-5 h-5 text-[#4fd1c5]" />
                <span>{aboutContent.facility.location}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {aboutContent.facility.stats.map((stat, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#4fd1c5]" />
                    <span className="text-gray-700">{stat}</span>
                  </li>
                ))}
              </ul>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-6 bg-white border border-gray-200">
                  <span className="text-3xl font-bold text-[#0a1628] block mb-1">50,000</span>
                  <span className="text-gray-500 text-sm">MT Annual Capacity</span>
                </div>
                <div className="p-6 bg-[#0a1628]">
                  <span className="text-3xl font-bold text-[#4fd1c5] block mb-1">GCC</span>
                  <span className="text-gray-400 text-sm">Regional Coverage</span>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://images.pexels.com/photos/1145434/pexels-photo-1145434.jpeg"
                alt="Facility"
                className="w-full aspect-video object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Focus Areas */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#4fd1c5] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
              {aboutContent.focus.headline}
            </p>
            <h2 className="text-3xl lg:text-4xl font-light text-[#0a1628]">
              Our <span className="font-semibold">Commitment</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Target, title: 'Sustainability', desc: aboutContent.focus.items[0] },
              { icon: Shield, title: 'IP Protection', desc: aboutContent.focus.items[1] },
              { icon: Eye, title: 'Transparency', desc: aboutContent.focus.items[2] },
            ].map((item, i) => (
              <div key={i} className="p-8 bg-[#f8fafc] hover:bg-white hover:shadow-lg transition-all border border-transparent hover:border-gray-200">
                <item.icon className="w-10 h-10 text-[#4fd1c5] mb-5" />
                <h3 className="text-xl font-semibold text-[#0a1628] mb-3">{item.title}</h3>
                <p className="text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 lg:py-24 bg-[#0a1628]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#4fd1c5] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
              Accredited & Certified
            </p>
            <h2 className="text-3xl lg:text-4xl font-light text-white mb-4">
              International <span className="font-semibold">Certifications</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Grenew FZE operates to the highest global standards, independently verified by Realcare Certification and accredited by the Emirates International Accreditation Centre (EIAC).
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {[
              {
                code: 'ISO 9001:2015',
                title: 'Quality Management',
                certNo: 'Q-01080001729',
                file: '/certificates/GRENEW FZE ISO 9001.pdf',
              },
              {
                code: 'ISO 14001:2015',
                title: 'Environmental Management',
                certNo: 'E-01080001730',
                file: '/certificates/GRENEW FZE ISO 14001.pdf',
              },
              {
                code: 'ISO 45001:2018',
                title: 'Occupational Health & Safety',
                certNo: 'O-01080001731',
                file: '/certificates/GRENEW FZE ISO 45001.pdf',
              },
              {
                code: 'ISO 27001:2022',
                title: 'Information Security',
                certNo: 'I-01080001732',
                file: '/certificates/GRENEW FZE ISO 27001.pdf',
              },
              {
                code: 'R2v3 Certified',
                title: 'Sustainable Electronics Reuse & Recycling',
                certNo: 'C2026-05529',
                file: '/certificates/GRENEW FZE R2v3.pdf',
              },
            ].map((cert) => (
              <div
                key={cert.code}
                className="group bg-white/5 border border-white/10 hover:border-[#4fd1c5]/50 p-6 flex flex-col transition-all"
              >
                <Award className="w-10 h-10 text-[#4fd1c5] mb-4" />
                <h3 className="text-lg font-semibold text-white mb-1">{cert.code}</h3>
                <p className="text-sm text-gray-400 mb-4">{cert.title}</p>
                <p className="text-xs text-gray-500 mb-5">Cert No. {cert.certNo}</p>
                <a
                  href={cert.file}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto inline-flex items-center gap-2 text-[#4fd1c5] hover:text-white text-sm font-semibold transition-colors"
                >
                  <Download className="w-4 h-4" />
                  View Certificate
                </a>
              </div>
            ))}
          </div>
          <p className="text-center text-gray-500 text-xs mt-8">
            ISO standards issued by Realcare Certification Pvt. Ltd. (EIAC accredited) · R2v3 issued by Perry Johnson Registrars, Inc. (ANAB accredited, SERI authorized)
          </p>
        </div>
      </section>

      {/* Industries & Clients */}
      <section className="py-16 lg:py-24 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <p className="text-[#4fd1c5] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
                Industries We Serve
              </p>
              <h3 className="text-2xl font-semibold text-[#0a1628] mb-6">Diverse Sectors</h3>
              <div className="flex flex-wrap gap-2">
                {industries.map((ind) => (
                  <span key={ind.name} className="px-4 py-2 bg-white border border-gray-200 text-gray-600 text-sm">
                    {ind.name}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[#4fd1c5] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
                Our Clients
              </p>
              <h3 className="text-2xl font-semibold text-[#0a1628] mb-6">Fortune 500 & Beyond</h3>
              <div className="flex flex-wrap gap-2">
                {clients.map((client) => (
                  <span key={client} className="px-4 py-2 bg-white border border-gray-200 text-gray-600 text-sm">
                    {client}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-[#0a1628]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-light text-white mb-6">
            Ready to <span className="font-semibold">Partner?</span>
          </h2>
          <p className="text-gray-400 mb-8">
            Join leading enterprises in building a sustainable future.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-[#4fd1c5] hover:bg-[#38b2ac] text-[#0a1628] px-8 py-4 font-semibold transition-colors">
              Request a Pickup
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/contact#partner" className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-white text-white px-8 py-4 font-semibold transition-colors">
              Partner With Us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
